/**
 * FlowAI Skill-Editor — Markdown + Frontmatter mit Live-Test.
 * SSoT: Skill-Name + Workspace als Form-Felder, alle Skill-Metadaten via Frontmatter im Markdown.
 *
 * @page /flowai/skills/new
 * @page /flowai/skills/:id
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, Card, Button } from '../../components/ui';
import FlowMdEditor from '../../components/FlowMdEditor';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

const SKILL_TEMPLATE = `---
skill_key: my_skill
inputs:
  - case_id
  - subject
outputs:
  - decision
  - reasoning
output_format: strikt JSON
model: claude-sonnet-4-20250514
temperature: 0.2
---

# Skill: Mein Skill

## Aufgabe
Beschreibe was der Skill tut.

## Inputs
- {{case_id}} — Case-ID
- {{subject}} — Betreff

## Prompt
Klassifiziere den folgenden Case…

Gib **strikt JSON** zurück:
\`\`\`json
{ "decision": "yes|no", "reasoning": "…" }
\`\`\`
`;

export default function FlowAISkillEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const [doc, setDoc] = useState(null);
  const [body, setBody] = useState(SKILL_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/flowai/workspaces`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setWorkspaces(Array.isArray(d) ? d : (d?.workspaces ?? [])))
      .catch(() => setWorkspaces([]));
  }, []);

  async function load() {
    if (isNew) {
      setDoc({
        name: 'Mein Skill',
        workspace_id: Number(search.get('workspace_id')) || null,
      });
      setBody(SKILL_TEMPLATE);
      return;
    }
    const r = await fetch(`${API}/api/flowai/skills/${id}`, { credentials: 'include' });
    const raw = await r.json().catch(() => ({}));
    const d = raw?.skill ?? raw ?? {};
    setDoc(d);
    setBody(d.content_md || SKILL_TEMPLATE);
  }
  useEffect(() => { load(); }, [id]);

  async function save() {
    if (!doc?.name?.trim()) {
      alert('Name ist Pflichtfeld');
      return;
    }
    setSaving(true);
    const payload = {
      name: doc.name,
      workspace_id: doc.workspace_id || null,
      content_md: body,
    };
    const url = isNew ? `${API}/api/flowai/skills` : `${API}/api/flowai/skills/${id}`;
    const method = isNew ? 'POST' : 'PUT';
    const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const raw = await r.json().catch(() => ({}));
    const out = raw?.skill ?? raw ?? {};
    setSaving(false);
    if (r.ok && isNew && out.id) navigate(`/flowai/skills/${out.id}`);
    else if (r.ok) load();
    else alert('Speichern fehlgeschlagen: ' + (raw.error || r.status));
  }

  if (!doc) return <div className="p-6 text-sm">Lade…</div>;

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[
        { label: 'Skills', to: '/flowai/skills' },
        { label: isNew ? 'Neuer Skill' : doc.name },
      ]} />
      <PageHeader
        title={isNew ? 'Neuer Skill' : doc.name}
        subtitle={isNew ? 'Name + Workspace setzen, Metadaten via YAML-Frontmatter im Markdown.' : `Skill #${doc.id}${doc.version ? ` · v${doc.version}` : ''}${doc.slug ? ` · ${doc.slug}` : ''}`}
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/flowai/skills')}>← Liste</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Speichere…' : 'Speichern'}</Button>
          </div>
        }
      />
      <Card className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Skill-Name (Pflicht)">
            <input value={doc.name || ''} onChange={(e) => setDoc({ ...doc, name: e.target.value })} placeholder="z.B. Email-Klassifikator" className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm" />
          </Field>
          <Field label="Workspace (leer = global)">
            <select
              value={doc.workspace_id || ''}
              onChange={(e) => setDoc({ ...doc, workspace_id: Number(e.target.value) || null })}
              className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm bg-white"
            >
              <option value="">— global (alle Workspaces) —</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>{w.name} (#{w.id})</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-3 text-[11px] text-[var(--text-tertiary,#6B7280)]">
          Skill-Key, Inputs, Outputs, Modell, Temperature werden via <code>---</code>-Frontmatter im Markdown gepflegt. Slug wird aus dem Namen automatisch generiert.
        </div>
      </Card>

      <Card title="Markdown-Body" subtitle="Frontmatter + Prompt — Single Source of Truth für Skill-Metadaten">
        <FlowMdEditor value={body} onChange={setBody} minHeight={500} />
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      {children}
    </label>
  );
}
